// Global search — APP results + zero-result suggestions.
//
// Nika 2026-09-10: "search should also search and suggest apps. if nothing
// found - it should suggest from appstore, marketplace or integrations".
// Two behaviours, both PURE over data so they unit-test without a DOM or a
// network (globalSearch.js owns the fetching and the state write):
//
//   1. `appSearchHits` — the apps this workspace ALREADY has (the built-in
//      SEARCH_APPS tiles plus every installed app / module tile) matched
//      against the query, so typing "cal" offers Calendar beside the record
//      hits. It takes the groups `searchAppGroupsFor(root)` already built, so
//      the feature-flag gate, the admin gate and every module tile's own
//      per-viewer `visible()` are ALREADY applied — search can never offer an
//      app the launcher itself hides for this viewer.
//
//   2. `appSuggestionHits` — what the menu shows when the query matched
//      NOTHING. An empty menu is a dead end, and the workspace can nearly
//      always ADD the thing that was just asked for. Three sources in
//      descending specificity: the App Store catalog (the same
//      `workspaceModule: true` projects read the /add-app rail renders), the
//      `integration_kinds` catalogue, and three always-present BROWSE rows
//      for the App Store / Marketplace / Integrations surfaces — so the menu
//      is never empty even for a query nothing in either catalogue matches.
//
// Row shape is deliberately DOUBLE-KEYED. `title` / `caption` / `icon` /
// `route` is the launcher tile contract `AppListItem` already renders (its
// Title/Caption resolve through one `el.call('polyglot', …)`, which returns a
// shipped key translated and a customer's own words verbatim — so the KEY is
// what travels here, never a pre-resolved string, and a language switch still
// re-renders). `_category` / `_id` / `_title` / `_subtitle` / `_href` is the
// search-result contract `_groupResults` groups and `moveNavSearchSelection`
// indexes. `_appRoute` is the marker that says "open me through the
// launcher's own opener": `NavSearchResultGroup` renders these rows with
// `NavSearchAppItem` and `activateNavSearchSelection` routes them through
// `selectAppFromSwitcher`, so a Calendar pick from search lands exactly where
// a Calendar pick from the apps launcher lands (canvas full-page, /admin
// full-page, everything else the PageSlot).

// THE catalog search rule (packages/shared/functions/catalogSearch.js) — the
// word / word-prefix / word-form match the App Store itself and the
// assistant's store search use. The App Store and integration suggestions
// below match through it, so "schedule" offers Scheduling here exactly as the
// store shows it. CALLED THROUGH THE ELEMENT (`opts.el.call`) — globalSearch
// hands its element in: a published shared function finds its own helpers
// through its receiver, and `_wsShared.functions.X(...)` gives it the
// functions bag instead, so it threw `__scope.catalogSearchCachedQueryWords is
// not a function` (design-d2's review, 2026-10-01; the parcel build never
// shows it). The wrapper below serves a call with no element (a test) — bare
// shared-library import + a deferred-call wrapper, never a deep subpath or a
// module-scope destructure (see app.js's P0 note).
import _wsShared from '@symbo-ls/workspace-shared'
const catalogSearchRank = (...args) => _wsShared.functions.catalogSearchRank(...args)
const _rankStoreRows = (el, rows, q, fields) =>
  el && typeof el.call === 'function'
    ? el.call('catalogSearchRank', rows, q, { fields })
    : catalogSearchRank(rows, q, { fields })

// Discovery entry points — the three surfaces a workspace adds capability
// from. ALWAYS returned by `appSuggestionHits`, matched query or not: they are
// the answer to "I looked for X and this workspace has no X".
//
// Every one of them is a MARKETPLACE surface, deliberately, and none is
// `/admin/integrations`: the admin hub renders only the kinds an org has
// already configured and is admin-gated, while discovery ("what could you
// connect?") is the marketplace's own Integrations surface — see
// pages/admin/integrations.js, which says so in its own header.
const _BROWSE_ROWS = [
  {
    id: 'browse-appstore',
    title: 'shell.launcher.appStore',
    caption: 'workspace.search.suggest.appStoreCaption',
    icon: 'blocks',
    route: '/marketplace?surface=appstore'
  },
  {
    id: 'browse-marketplace',
    title: 'workspace.search.suggest.marketplace',
    caption: 'workspace.search.suggest.marketplaceCaption',
    icon: 'library',
    route: '/marketplace'
  },
  {
    id: 'browse-integrations',
    title: 'workspace.search.suggest.integrations',
    caption: 'workspace.search.suggest.integrationsCaption',
    icon: 'api',
    route: '/marketplace?surface=integrations'
  }
]

// Rank a needle against a row's searchable fields. 0 = a field STARTS with the
// query (a strong, "you meant this" match), 1 = a field merely contains it,
// -1 = no field matched at all. Two buckets rather than a fuzzy score on
// purpose: the caller caps at five rows, and a prefix hit is the only ordering
// distinction that reliably reads as correct to a human ("mail" must not put
// "Broadcast — send mail to the team" above "Mail").
const _rank = (needle, fields) => {
  let best = -1
  for (const f of fields || []) {
    const v = String(f || '').toLowerCase()
    if (!v) continue
    const at = v.indexOf(needle)
    if (at < 0) continue
    const score = at === 0 ? 0 : 1
    if (best < 0 || score < best) best = score
    if (best === 0) return 0
  }
  return best
}

// What an App Store row and an integration kind are searched on — the
// server's store fields as far as each row carries them (name 4, category 2,
// key 2, description 1). HOW they match is the shared rule's, not this file's.
//
// An App Store row's name and line are ALSO searched in the words the member
// READS: the session language, through THE store-copy resolver — the call the
// row's own title and caption make (AppListItem) — beside the record's own
// words, which stay. The App Store's own search does the same
// (packages/marketplace/functions/index.js `_storeSearchFields`). The record
// carries English only, so a Georgian query matched no App Store app by the
// name or the line its row shows (lane DESC, gap 3). The shared rule keeps a
// word once, at its best field's weight, so a shown word that equals a record
// word adds nothing. With no element (a test, a bare call) only the record's
// own words are searched. The read is guarded like globalSearch's own name
// read (`_name`): an element that cannot answer the resolver — it throws —
// costs the shown words, never the suggestions.
const _storeSuggestFields = (el) => {
  const shown = (a, field) => {
    if (!a || !el || typeof el.call !== 'function') return ''
    try {
      return el.call('storeItemText', a, field) || ''
    } catch (_) {
      return ''
    }
  }
  return [
    { get: (a) => (a && a.name) || '', weight: 4 },
    { get: (a) => shown(a, 'name'), weight: 4 },
    { get: (a) => (a && a.category) || '', weight: 2 },
    { get: (a) => (a && a.key) || '', weight: 2 },
    { get: (a) => (a && a.description) || '', weight: 1 },
    { get: (a) => shown(a, 'description'), weight: 1 }
  ]
}
const _integrationSuggestFields = () => [
  { get: (k) => (k && k.label) || '', weight: 4 },
  { get: (k) => (k && k.category) || '', weight: 2 },
  { get: (k) => (k && k.kind) || '', weight: 2 },
  { get: (k) => (k && k.description) || '', weight: 1 }
]

// Stable sort: rank first, then the source order (SEARCH_APPS' own order,
// which is the launcher's curated order — Work before Team before Insights).
const _byRank = (a, b) => a.rank - b.rank || a.order - b.order

// One row, in both contracts at once. See the header for why it is double-keyed.
//
// `appKey` rides along when the tile names an app RECORD — an installed app's
// one launcher tile (workspaceConfigs.js stamps it) or an App Store row (its
// own key). AppListItem's title reads the name through THE store-copy
// resolver with it, so the row says what the App Store says (Nika 2026-10-23:
// "app names = what a person sees in the workspace App Store"). A tile with no
// record — a house tile, an app's sub-surface, a customer's own — carries none
// and reads exactly as before.
//
// `captionAppKey` rides along only on an App Store row: its caption is the
// RECORD's one line, so AppListItem's caption reads it through the same
// resolver with this key — the store's line in the session language (lane
// DESC, gap 3). `appKey` cannot say it: an installed app's tile carries
// `appKey` for its name, and its caption is the app's own.
const _appRow = (category, tile, id) => ({
  _category: category,
  _id: id || tile.route || tile.title,
  _title: tile.title,
  _subtitle: tile.caption || '',
  _href: tile.route,
  _appRoute: tile.route,
  title: tile.title,
  caption: tile.caption || '',
  icon: tile.icon || 'blocks',
  iconBg: tile.iconBg,
  route: tile.route,
  ...(tile.appKey ? { appKey: tile.appKey } : {}),
  ...(tile.captionAppKey ? { captionAppKey: tile.captionAppKey } : {})
})

// The apps this workspace already has, matched against the query.
//
// `groups` is `searchAppGroupsFor(root)`'s output (already gated — see the
// header). `translate` resolves a shipped polyglot key so a Georgian session
// matches Georgian words; the RAW key and the route are matched too, so
// "/notes" and "notes" both answer in any language and a customer's own tile
// (whose title is their words, never a key) matches unchanged.
//
// `name` is the name a member READS for a tile: globalSearch passes THE
// store-copy resolver, so an installed app answers to its App Store name —
// "insights" finds the tile whose manifest says "Analytics" — beside the words
// it already matched on.
export const appSearchHits = (groups, query, opts) => {
  const q = String(query || '')
    .trim()
    .toLowerCase()
  if (!q) return []
  const options = opts || {}
  const translate = typeof options.translate === 'function' ? options.translate : (k) => k
  const name = typeof options.name === 'function' ? options.name : () => ''
  const limit = options.limit || 5
  // `userApps` (root._userApps): the person's own apps join as a last group,
  // Your apps (personAppTiles below), so the caller needs one helper only.
  const base = groups || []
  const yours = options.userApps
    ? personAppTiles(
        options.userApps,
        base.flatMap((g) => (g && g.apps) || [])
      )
    : []
  const all = yours.length
    ? [...base, { key: 'your-apps', label: 'workspace.addApp.apps.yours', apps: yours }]
    : base
  const seen = new Set()
  const scored = []
  for (const g of all) {
    for (const a of (g && g.apps) || []) {
      if (!a || !a.route) continue
      // Dedupe by ROUTE: the same destination legitimately appears in more
      // than one launcher group (SEARCH_APPS lists /team-members twice, as
      // "Team members" and as "Users"), and two rows opening the same page is
      // a worse answer than one.
      const key = String(a.route).toLowerCase()
      if (seen.has(key)) continue
      const rank = _rank(q, [
        name(a),
        translate(a.title),
        translate(a.caption),
        a.title,
        String(a.route).replace(/^\//, '')
      ])
      if (rank < 0) continue
      seen.add(key)
      scored.push({ rank, order: scored.length, row: _appRow('apps', a) })
    }
  }
  return scored
    .sort(_byRank)
    .slice(0, limit)
    .map((s) => s.row)
}

// THE PERSON'S OWN APPS (persona round 1b, BLD-28: "Ctrl+K for 'tutor shift'
// answers NOTHING FOUND IN THIS WORKSPACE, so the draft app is not findable
// once I leave the builder"). The apps the builder lists under Your apps
// (`root._userApps`, private and shared) as launcher tiles: the app's own
// name, "Your apps" as the caption, and the address that opens it in its
// builder thread (`/add-app?thread=<id>` — functions/searchApps.js binds the
// thread wherever the builder opens). An app already IN the workspace has its
// own tile (`/x/<id>`, its page) among the workspace apps, so it is left out
// here: one row per app. → the tiles, newest first.
export const personAppTiles = (userApps, workspaceTiles) => {
  const inWorkspace = new Set()
  for (const tile of workspaceTiles || []) {
    const hit = /^\/x\/([^/?#]+)/.exec(String((tile && tile.route) || ''))
    if (hit) inWorkspace.add(decodeURIComponent(hit[1]))
  }
  const at = (app) => {
    const raw = app.updatedAt || app.updated_at || app.createdAt || app.created_at
    const t = typeof raw === 'number' ? raw : Date.parse(raw || '')
    return Number.isFinite(t) ? t : 0
  }
  return (Array.isArray(userApps) ? userApps : [])
    .filter((app) => app && app.id && String(app.name || '').trim())
    .filter((app) => !inWorkspace.has(String(app.id)))
    .map((app, i) => ({ app, t: at(app), i }))
    .sort((a, b) => b.t - a.t || a.i - b.i)
    .map(({ app }) => ({
      title: String(app.name).trim(),
      caption: 'workspace.addApp.apps.yours',
      icon: app.appKind === 'multipage' ? 'layers' : 'blocks',
      route: '/add-app?thread=' + encodeURIComponent(String(app.id))
    }))
}

// What to offer when the query matched nothing. `catalog` is
// `root._wsAppsCatalog` ({ id, owner, key, name, category, description } —
// loadWorkspaceAppsCatalog's shape) and `integrationKinds` is
// `listIntegrationKinds()`'s ({ kind, label, category, description, … }).
// Either may be empty (a cold cache, a failed read, a one-character query
// globalSearch declined to fetch for) — the BROWSE rows are unconditional, so
// this never returns an empty list and the menu is never a dead end. `el` is
// the caller's element: the store rule (see the import note) and each App
// Store row's link run through it.
export const appSuggestionHits = (query, opts) => {
  const q = String(query || '')
    .trim()
    .toLowerCase()
  const options = opts || {}
  const limit = options.limit || 5
  const rows = []

  // Both catalogues rank through the shared rule (see the import note): the
  // matching rows only, best first, the catalogue's own order between equals.
  const storeRows = (options.catalog || []).filter((a) => a && a.key)
  const store = q ? _rankStoreRows(options.el, storeRows, q, _storeSuggestFields(options.el)) : []
  // A row's App Store page — the App Store's own item deep link, from its one
  // builder (marketplace functions/appStoreItemLink.js, registered at boot),
  // which the /add-app catalog card's Details link asks too
  // (components/AddAppCatalogCard.js): a suggestion and the rail agree on
  // where an app lives. Asked through the element, like the store rule above,
  // and guarded like the shown words: with no element (a test, a bare call),
  // or one that cannot answer, the row has no link, never no row.
  const storeLink = (a) => {
    const el = options.el
    if (!el || typeof el.call !== 'function') return null
    try {
      return el.call('appStoreItemLink', a.owner, a.key) || null
    } catch (_) {
      return null
    }
  }
  rows.push(
    ...store.slice(0, limit).map((a) =>
      // The caption travels as the record's words and names its record
      // (`captionAppKey`, see `_appRow`), so it resolves where it renders,
      // like the title.
      _appRow(
        'appstore',
        {
          title: a.name || a.key,
          caption: a.description || 'workspace.search.suggest.appStoreRow',
          icon: 'blocks',
          route: storeLink(a),
          appKey: a.key,
          captionAppKey: a.key
        },
        `appstore:${a.owner}/${a.key}`
      )
    )
  )

  const kindRows = (options.integrationKinds || []).filter((k) => k && k.kind)
  const integrations = q ? _rankStoreRows(options.el, kindRows, q, _integrationSuggestFields()) : []
  rows.push(
    ...integrations.slice(0, limit).map((k) =>
      // Every integration row lands on the marketplace's Integrations
      // SURFACE rather than a per-kind deep link. The per-kind marketplace id
      // (`int.<slug>`, with three overrides) is the marketplace package's own
      // mapping and the shell must not grow a second copy of it, and the
      // /admin/integrations/<kind> detail pages are an explicit route map
      // that a catalogue kind can be absent from — a suggestion that 404s is
      // worse than one that lands on the shelf it lives on.
      _appRow(
        'integrations',
        {
          title: k.label || k.kind,
          caption: k.description || 'workspace.search.suggest.integrationRow',
          icon: 'api',
          route: '/marketplace?surface=integrations'
        },
        `integration:${k.kind}`
      )
    )
  )

  rows.push(..._BROWSE_ROWS.map((b) => _appRow('discover', b, b.id)))
  return rows
}
